import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Zap } from 'lucide-react'
import { useState } from 'react'

import { ruleQuery, type InteractionRule } from '@/api/actions'

import { Bullets, StepList } from './ActionDetail'
import { describeRule, eventLabel, ruleTitle } from './describe'
import { DetailFrame, DetailLoading, DetailMissing, SummarySection } from './DetailFrame'
import { DocEditor } from './editor/DocEditor'
import { consumeJustCreated } from './editor/NewDocPage'
import { RoleChip } from './parts'
import { PreviewPanel } from './PreviewPanel'

/**
 * `/actions/$id?kind=rule` — one world rule in plain words: **when** (the event and the two things
 * it happens between), **then** (what the world does, step by step), **afterwards** (what is now
 * true), and a preview: a little scene in which the event happens (`POST /api/rules/{id}/preview`).
 * Same frame as an action: switch, duplicate, delete, JSON under Advanced; `?edit=1` opens the
 * manual editor beside the preview.
 */

export function RuleDetail({ id, editing = false, validateDelay }: { id: string; editing?: boolean; validateDelay?: number }) {
  const q = useQuery(ruleQuery(id))
  const navigate = useNavigate()
  const [saves, setSaves] = useState(0)
  const [fresh] = useState(() => consumeJustCreated('rule', id))
  if (q.isPending) return <DetailLoading what="world rule" />
  if (q.isError || !q.data) return <DetailMissing kind="rule" error={q.error} />
  const rule = q.data
  const preview = (
    <div className="card flex flex-col gap-3 p-5">
      <h2 className="text-lg font-semibold text-ink">See it happen</h2>
      {editing ? <p className="text-xs text-ink-3">Plays the saved version — save to try your changes.</p> : null}
      <PreviewPanel kind="rule" action={{ id: rule.id, label: ruleTitle(rule), roles: {} }} replayKey={saves} autoPlay={fresh} />
    </div>
  )
  return (
    <DetailFrame kind="rule" doc={rule} title={ruleTitle(rule)} tag={rule.when?.event ? `When: ${eventLabel(rule.when.event).toLowerCase()}` : null} onReload={() => void q.refetch()} editing={editing}>
      {editing ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
          <DocEditor
            key={`${rule.id}:${rule.rev}:${rule.source ?? ''}`}
            kind="rule"
            mode="edit"
            doc={rule}
            validateDelay={validateDelay}
            onSaved={() => setSaves((n) => n + 1)}
            onReload={() => void q.refetch()}
            onClose={() => void navigate({ to: '/actions/$id', params: { id: rule.id }, search: { kind: 'rule' } })}
          />
          <aside className="flex min-w-0 flex-col gap-4 xl:sticky xl:top-20 xl:self-start">{preview}</aside>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <RuleSummaryView rule={rule} />
          {preview}
        </div>
      )}
    </DetailFrame>
  )
}

export function RuleSummaryView({ rule }: { rule: InteractionRule }) {
  const sum = describeRule(rule)
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="rule-summary">
      <div className="card flex flex-col gap-4 p-5">
        <h2 className="text-lg font-semibold text-ink">When</h2>
        <p className="flex items-start gap-2 text-lg text-ink" data-testid="rule-when">
          <Zap className="mt-1 size-5 shrink-0 text-accent" aria-hidden />
          <span>{sum.when}</span>
        </p>
        {sum.subject || sum.other ? (
          <SummarySection title="Between" testId="rule-between">
            <ul className="flex flex-col gap-2 text-sm">
              {rule.when?.subject ? (
                <li className="flex flex-wrap items-center gap-2">
                  <RoleChip name="subject" spec={rule.when.subject} />
                  <span className="text-ink-2">{sum.subject}</span>
                </li>
              ) : null}
              {rule.when?.other ? (
                <li className="flex flex-wrap items-center gap-2">
                  <RoleChip name="other" spec={rule.when.other} />
                  <span className="text-ink-2">{sum.other}</span>
                </li>
              ) : null}
            </ul>
          </SummarySection>
        ) : null}
        {sum.priority ? <p className="text-xs text-ink-3">{sum.priority}</p> : null}
      </div>
      <div className="card flex flex-col gap-5 p-5">
        <h2 className="text-lg font-semibold text-ink">Then</h2>
        <SummarySection title="What the world does" testId="rule-then">
          <StepList steps={sum.then} />
        </SummarySection>
        {sum.after.length ? (
          <SummarySection title="Afterwards" testId="rule-after">
            <Bullets items={sum.after} />
          </SummarySection>
        ) : null}
      </div>
    </div>
  )
}
